import {
  App,
  Button,
  Card,
  Dropdown,
  Flex,
  Input,
  Select,
  Tag,
  Typography,
  theme
} from 'antd'
import type { InputRef, MenuProps } from 'antd'
import { Plus, X } from 'lucide-react'
import {
  forwardRef,
  useImperativeHandle,
  useRef,
  useState,
  type ClipboardEvent,
  type KeyboardEvent
} from 'react'
import { useTranslation } from 'react-i18next'

import { isEmailValid } from 'src/utils/string'
import { MutableWorkspaceRole } from 'src/utils/workspaceAuthorization'

import type { PendingInvite } from './types'

const SPLIT_RE = /[\s,;\n\t]+/

export type EmailInviteFormHandle = {
  /** Promote any unconfirmed text in the input into a chip. */
  commitDraft: () => void
}

type EmailInviteFormProps = {
  invites: PendingInvite[]
  onAdd: (email: string, orgRole: MutableWorkspaceRole) => void
  onRemove: (inviteId: string) => void
  onUpdateRole?: (inviteId: string, orgRole: MutableWorkspaceRole) => void
}

export const EmailInviteForm = forwardRef<
  EmailInviteFormHandle,
  EmailInviteFormProps
>(({ invites, onAdd, onRemove, onUpdateRole }, ref) => {
  const { t } = useTranslation()
  const { message } = App.useApp()
  const { token } = theme.useToken()
  const inputRef = useRef<InputRef>(null)
  const [draft, setDraft] = useState('')
  const [focused, setFocused] = useState(false)
  const [defaultRole, setDefaultRole] = useState<MutableWorkspaceRole>(
    MutableWorkspaceRole.EMPLOYEE
  )

  const orgRoleOptions = [
    {
      value: MutableWorkspaceRole.ADMIN,
      label: t('workspaces.create.invite.orgRole.admin')
    },
    {
      value: MutableWorkspaceRole.EMPLOYEE,
      label: t('workspaces.create.invite.orgRole.employee')
    }
  ]

  const tryCommit = (raw: string) => {
    const candidates = raw
      .split(SPLIT_RE)
      .map((s) => s.trim())
      .filter(Boolean)
    const invalid: string[] = []
    const seen = new Set(invites.map((i) => i.email.toLowerCase()))
    candidates.forEach((email) => {
      const lower = email.toLowerCase()
      if (seen.has(lower)) return
      if (!isEmailValid(email)) {
        invalid.push(email)
        return
      }
      seen.add(lower)
      onAdd(email, defaultRole)
    })
    if (invalid.length > 0) {
      message.warning(
        t('workspaces.create.invite.invalidEmails', {
          emails: invalid.join(', ')
        })
      )
    }
  }

  useImperativeHandle(ref, () => ({
    commitDraft: () => {
      if (draft.trim()) {
        tryCommit(draft)
        setDraft('')
      }
    }
  }))

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (
      event.key === 'Enter' ||
      event.key === ',' ||
      event.key === ';' ||
      (event.key === 'Tab' && draft.trim())
    ) {
      if (!draft.trim()) return
      event.preventDefault()
      tryCommit(draft)
      setDraft('')
      return
    }
    if (event.key === 'Backspace' && !draft && invites.length > 0) {
      onRemove(invites[invites.length - 1].id)
    }
  }

  const handlePaste = (event: ClipboardEvent<HTMLInputElement>) => {
    const text = event.clipboardData.getData('text')
    if (!text) return
    if (SPLIT_RE.test(text)) {
      event.preventDefault()
      tryCommit(draft + text)
      setDraft('')
    }
  }

  const handleAddClick = () => {
    if (!draft.trim()) {
      inputRef.current?.focus()
      return
    }
    tryCommit(draft)
    setDraft('')
    inputRef.current?.focus()
  }

  const buildRoleMenu = (inviteId: string): MenuProps => ({
    items: orgRoleOptions.map((opt) => ({
      key: opt.value,
      label: opt.label,
      onClick: () => onUpdateRole?.(inviteId, opt.value)
    }))
  })

  return (
    <Card
      title={t('workspaces.create.invite.byEmail')}
      size="small"
      variant="outlined"
    >
      <Flex orientation="vertical" gap={12}>
        <Flex align="center" gap={8} wrap="wrap">
          <Typography.Text style={{ fontSize: 13 }}>
            {t('workspaces.create.invite.defaultRole')}:
          </Typography.Text>
          <Select
            value={defaultRole}
            onChange={setDefaultRole}
            options={orgRoleOptions}
            size="small"
            style={{ width: 180 }}
          />
        </Flex>

        <div
          onClick={() => inputRef.current?.focus()}
          // This box turns its own border on focus, so antd's inset outline on the
          // borderless field inside would be a second ring.
          className="ui-owns-focus-ring"
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 6,
            alignItems: 'center',
            padding: '6px 8px',
            minHeight: 40,
            borderRadius: token.borderRadius,
            border: `1px solid ${focused ? token.colorPrimary : token.colorBorder}`,
            background: token.colorBgContainer,
            cursor: 'text',
            transition: 'border-color 0.2s'
          }}
        >
          {invites.map((invite) => (
            <Tag
              key={invite.id}
              closable
              closeIcon={<X size={12} />}
              onClose={(event) => {
                event.preventDefault()
                onRemove(invite.id)
              }}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
                margin: 0,
                padding: '2px 6px'
              }}
            >
              <span>{invite.email}</span>
              {onUpdateRole ? (
                <Dropdown
                  menu={buildRoleMenu(invite.id)}
                  trigger={['click']}
                  placement="bottomLeft"
                >
                  <Tag
                    color="processing"
                    style={{
                      cursor: 'pointer',
                      margin: 0,
                      lineHeight: '16px'
                    }}
                    onClick={(event) => event.stopPropagation()}
                  >
                    {t(`workspaces.create.invite.orgRole.${invite.orgRole}`)}
                  </Tag>
                </Dropdown>
              ) : (
                <Tag
                  color="processing"
                  style={{ margin: 0, lineHeight: '16px' }}
                >
                  {t(`workspaces.create.invite.orgRole.${invite.orgRole}`)}
                </Tag>
              )}
            </Tag>
          ))}
          <Input
            ref={inputRef}
            variant="borderless"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            onFocus={() => setFocused(true)}
            onBlur={() => {
              setFocused(false)
              if (draft.trim()) {
                tryCommit(draft)
                setDraft('')
              }
            }}
            placeholder={
              invites.length === 0
                ? t('workspaces.create.invite.chipPlaceholder')
                : t('workspaces.create.invite.chipPlaceholderMore')
            }
            style={{
              flex: '1 1 200px',
              minWidth: 160,
              padding: 0,
              boxShadow: 'none'
            }}
          />
        </div>

        <Flex justify="space-between" align="center" wrap="wrap" gap={8}>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {t('workspaces.create.invite.chipHint')}
          </Typography.Text>
          <Flex align="center" gap={8}>
            {invites.length > 0 && (
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {t('workspaces.create.invite.pending', {
                  count: invites.length
                })}
              </Typography.Text>
            )}
            <Button
              size="small"
              icon={<Plus size={14} />}
              onClick={handleAddClick}
              disabled={!draft.trim()}
            >
              {t('workspaces.create.invite.add')}
            </Button>
          </Flex>
        </Flex>
      </Flex>
    </Card>
  )
})

EmailInviteForm.displayName = 'EmailInviteForm'
